// Front-end client for the NetSuite proxy API (api/). All calls go to the
// same-origin /api/* routes served by Azure Static Web Apps — no secrets and no
// CORS in the browser; the managed Function holds the credentials.
//
// Not yet wired into the operation runtime. Once `GET /api/ns-ping` returns ok,
// hook nsItem() into the stubbed getData/getRuleset in src/util/runtime.ts so
// "Populate Material Table" resolves real items.

/** Exported so errorLog.ts posts to the same place, rather than keeping its own copy of
 *  the base path that could drift from this one. */
import { getLaunchContext } from "./launchContext";

export const API_BASE = "/api";

export type NsItem = {
  id: string | number;
  itemid?: string;
  displayname?: string;
  itemtype?: string;
};

/**
 * Fire a POST that keeps running after the page/tab is closed (fetch keepalive),
 * for background work we don't wait on (BOM/routing, CPN, customer requirements).
 * Best-effort: no response handling, never throws. Keep bodies modest — keepalive
 * has a ~64KB combined limit.
 */
export function fireBackground(path: string, body: unknown): void {
  try {
    void fetch(`${API_BASE}${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* ignore — best effort */
  }
}

/** Build the BOM + routing for an already-created item, in the background.
 *
 *  Fired keepalive, so the request completes server-side even though the caller closes
 *  the cpq2 tab to return to the sales order a moment later. Nothing here can observe a
 *  failure -- there is no tab left to observe it in -- which is why finish-assembly
 *  records its own outcome to cpq2_error_log rather than relying on the caller. See
 *  recordBackgroundFailure there before making this awaited again. */
export function finishAssemblyInBackground(payload: {
  itemId: string | number;
  bom?: unknown;
  routing?: unknown;
  rebuild?: boolean;
  /** Mapping fields create-assembly left off the POST, PATCHed before the BOM. */
  fields?: Record<string, unknown>;
  /** The config-map row create-assembly planned but left for phase 2. Written only after
   *  the item exists, which finish-assembly guarantees by construction. */
  configMapRow?: Record<string, unknown> | null;
  /** Which assembly subtype the item was created as. Phase 2 PATCHes the same record, and
   *  a PATCH against the wrong subtype 400s after the BOM already exists. Omitted, the
   *  server reads it off the record instead. */
  recordType?: string | null;
  /** The list price create-assembly left for phase 2 (its pendingPrice). Applied after the
   *  deferred fields; the server validates it and the write is idempotent. */
  price?: PendingPrice | null;
  actor?: { name: string | null; id: string | null };
}): void {
  fireBackground("/finish-assembly", { ...payload, dryRun: false });
}

/**
 * Build the BOM + routing for an item and WAIT for the result. Throws on
 * failure. finish-assembly is idempotent by stable internal ids, checked
 * independently for the BOM (the item's attached-BOM id) and the routing (the
 * BOM's id) — names embed the volatile generated id, so they can't be trusted.
 * It backfills whichever piece is missing (e.g. a deleted routing on a kept BOM)
 * without duplicating one that exists. Safe to call for updated items and retry.
 */
export async function finishAssembly(payload: {
  itemId: string | number;
  bom?: unknown;
  routing?: unknown;
  // Edit Config: replace the existing BOM revision's components + routing steps
  // with this config's (instead of the default backfill-only, which would leave
  // the stale BOM/routing on an edited item).
  rebuild?: boolean;
  /** The launching operator, for the BOM and revision memos. Recorded as "claimed:" —
   *  it comes off the launch URL, so it is asserted rather than verified. */
  actor?: { name: string | null; id: string | null };
}): Promise<{ ok: boolean; bom?: { id: string } | null; routing?: { id: string } | null }> {
  const res = await fetch(`${API_BASE}/finish-assembly`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ ...payload, dryRun: false }),
  });
  const body = (await res.json().catch(() => ({}))) as {
    ok?: boolean;
    bom?: { id: string } | null;
    routing?: { id: string } | null;
    error?: string;
    step?: string;
  };
  if (!res.ok || body.ok === false) {
    throw new Error(
      `Building BOM/routing failed (HTTP ${res.status})${body.step ? ` at "${body.step}"` : ""}: ${
        body.error ?? res.statusText
      }`
    );
  }
  return body as { ok: boolean; bom?: { id: string } | null; routing?: { id: string } | null };
}

async function getJson<T>(path: string): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    headers: { Accept: "application/json" },
  });
  const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok || body.ok === false) {
    throw new Error(
      `NetSuite API ${path} failed (HTTP ${res.status}): ${
        (body.error as string) ?? res.statusText
      }`
    );
  }
  return body as T;
}

// ---- Existing-item check (by config fingerprint) ----
export type ItemMatch = { id: string; itemid: string | null };

/**
 * Does an item ALREADY exist in NetSuite for this config? Matches on
 * custitem_fingerprint = the generated ID key (HIDDEN_ID_KEY). Used by the header
 * badge to show "Existing Config" AND by Save & Submit to short-circuit the build
 * (bring in the existing item, build no new item/BOM/routing). Best-effort:
 * resolves to null on any proxy failure (so we just fall back to Editing/New).
 */
export async function findItemByFingerprint(fingerprint: string): Promise<ItemMatch | null> {
  const fp = fingerprint.trim();
  if (!fp) return null;
  try {
    const body = await getJson<{ ok: boolean; exists: boolean; item: ItemMatch | null }>(
      `/find-item?fingerprint=${encodeURIComponent(fp)}`
    );
    return body.ok && body.exists && body.item ? body.item : null;
  } catch {
    return null;
  }
}

/** The part number this configuration WOULD get, resolved ahead of time. */
export type PlannedName = {
  name: string;
  baseId: string;
  cpqNum: string | null;
  hash: string;
  index: number;
};

/** The existence check AND the pre-resolved name in one call.
 *
 *  Same endpoint the badge already polls, with ?plan=1 so it also resolves the name the
 *  configuration would be given. Doing it here means the four SuiteQL round trips happen
 *  while the user is still reading the form instead of while they wait for their lines.
 *
 *  The name is a HINT: create-assembly re-verifies it against the live account and resolves
 *  from scratch if anything has moved. Best-effort, like the plain lookup -- on any failure
 *  the caller gets nulls and everything behaves exactly as it did before. */
export async function findItemAndPlanName(
  fingerprint: string
): Promise<{ item: ItemMatch | null; plannedName: PlannedName | null }> {
  const fp = fingerprint.trim();
  if (!fp) return { item: null, plannedName: null };
  try {
    const body = await getJson<{
      ok: boolean; exists: boolean; item: ItemMatch | null; plannedName?: PlannedName;
    }>(`/find-item?plan=1&fingerprint=${encodeURIComponent(fp)}`);
    if (!body.ok) return { item: null, plannedName: null };
    return {
      item: body.exists && body.item ? body.item : null,
      plannedName: body.plannedName ?? null,
    };
  } catch {
    return { item: null, plannedName: null };
  }
}

/** Fill in an existing item's base price from the NDB price list — used by the
 *  Existing Config path, where nothing is built but the matched item may still
 *  carry no real price. Server-side this ONLY patches basePrice, skips items
 *  locked against CPQ edits, and leaves any real existing price alone (see the
 *  priceOnly branch in api/create-assembly). Best-effort: never throws, so it
 *  can't block a submit. */
export async function setItemBasePrice(
  itemId: string | number,
  price: number
): Promise<{ priced: boolean; reason?: string; previousPrice?: number | null }> {
  try {
    const res = await fetch(`${API_BASE}/create-assembly`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({
        priceOnly: true,
        editItemId: String(itemId),
        basePrice: price,
        // create-assembly requires an item shape even on this path; unused here.
        item: { itemid: "", fields: {} },
      }),
    });
    const body = (await res.json().catch(() => ({}))) as {
      ok?: boolean;
      priced?: boolean;
      reason?: string;
      previousPrice?: number | null;
      error?: string;
    };
    if (!res.ok || body.ok === false) return { priced: false, reason: body.error || `HTTP ${res.status}` };
    return { priced: body.priced === true, reason: body.reason, previousPrice: body.previousPrice ?? null };
  } catch (e) {
    return { priced: false, reason: e instanceof Error ? e.message : String(e) };
  }
}

/** Refresh the DERIVED TEXT (sales description, manufacturing notes) on an item that
 *  already matches this configuration.
 *
 *  The Existing Config path builds nothing, which is right — but it also meant the item's
 *  text was never rewritten, so an item created before a description rule changed kept the
 *  old wording forever: the fingerprint matched, so nothing ran. The server patches only
 *  the whitelisted text fields, never a locked item, and never the name.
 *
 *  Best-effort by contract, like setItemBasePrice: it resolves rather than throws, so a
 *  failed refresh can never turn a good submit into an error. */
export async function refreshItemText(
  itemId: string | number,
  fields: Record<string, string>,
  // Only when itemId is the item this Edit Config session opened: its config snapshot and
  // finished flag (custitem_unfinishedconfiguration) are rewritten with the text.
  snapshot?: { config: string; unfinished: boolean }
): Promise<{ refreshed: boolean; fields?: string[]; reason?: string; configWritten?: boolean }> {
  try {
    const res = await fetch(`${API_BASE}/create-assembly`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({
        fieldsOnly: true,
        editItemId: String(itemId),
        dryRun: false,
        // The server derives the patch from the ITCR body, so it needs the real fields.
        item: { itemid: "", fields },
        ...(snapshot ? { config: snapshot.config, unfinished: snapshot.unfinished } : {}),
      }),
    });
    const body = (await res.json().catch(() => ({}))) as {
      ok?: boolean;
      refreshed?: boolean;
      fields?: string[];
      reason?: string;
      configWritten?: boolean;
      error?: string;
    };
    if (!res.ok || body.ok === false) return { refreshed: false, reason: body.error || `HTTP ${res.status}` };
    return {
      refreshed: body.refreshed === true,
      fields: body.fields,
      reason: body.reason,
      configWritten: body.configWritten === true,
    };
  } catch (e) {
    return { refreshed: false, reason: e instanceof Error ? e.message : String(e) };
  }
}

/** Liveness + whether the proxy's app settings are configured. */
export function nsHealth(): Promise<{
  ok: boolean;
  configured: boolean;
  settingsPresent: Record<string, boolean>;
}> {
  return getJson("/health");
}

/** Server-side NetSuite connectivity check (token + a REST call). */
export function nsPing(): Promise<{
  ok: boolean;
  tokenAcquired: boolean;
  netsuiteStatus: number;
  message: string;
}> {
  return getJson("/ns-ping");
}

/** Look up an item by internal id. Resolves to null when not found. */
export async function nsItem(internalId: string | number): Promise<NsItem | null> {
  try {
    const body = await getJson<{ ok: boolean; item: NsItem }>(
      `/ns-item?id=${encodeURIComponent(String(internalId))}`
    );
    return body.item ?? null;
  } catch {
    return null;
  }
}

export type CreateAssemblyResult = {
  ok: boolean;
  dryRun?: boolean;
  /** `cannotEdit` is the server saying this item is flagged custitem_cannoteditcpq, so it was
   *  handed back untouched rather than patched. The caller must NOT adopt such an item as its
   *  edit target: a locked item is the one case where a further change should make a NEW
   *  part, which is the rule Kevin set on 2026-08-27. */
  item?: {
    id: string | null;
    created?: boolean;
    existing?: boolean;
    updated?: boolean;
    /** The server turned what this client sent as a CREATE into an edit of the launched item
     *  (the same-base backstop). Diagnostic — `updated` is the flag to branch on. */
    adoptedLaunchedItem?: boolean;
    itemid?: string;
    cannotEdit?: boolean;
  };
  bom?: { id: string } | null;
  revision?: { id: string };
  routing?: { id: string };
  step?: string;
  plan?: Record<string, unknown>;
  /** Which assembly subtype the item was created as — `assemblyItem`,
   *  `lotNumberedAssemblyItem`, or `serializedAssemblyItem` for the configurators that
   *  still use it. Passed straight to finish-assembly, which PATCHes the same record and
   *  would 400 against the wrong endpoint. */
  recordType?: string | null;
  /** Mapping fields deliberately left off the create POST, to be PATCHed by
   *  finish-assembly. Absent when deferFields was not asked for, when there was
   *  nothing to defer, or when the trimmed body was rejected and the server fell
   *  back to the full one (deferFellBack). */
  deferredFields?: Record<string, unknown>;
  deferredFieldCount?: number;
  deferFellBack?: boolean;
  /** True when the pre-resolved name survived the server's re-verification. */
  usedNameHint?: boolean;
  /** The config-map row phase 2 must write (itemOnly path). */
  configMapRow?: Record<string, unknown> | null;
  /** The price write the server left for finish-assembly, when deferPrice was asked for and
   *  there is a list price. Absent from an older server, which prices synchronously and
   *  reports it in `price` instead. */
  pendingPrice?: PendingPrice | null;
  /** What the server did about the price itself, when it did not defer it. */
  price?: { set?: boolean; skipped?: boolean; unchanged?: boolean; error?: string } | null;
  /** Set when an admin override code WAS sent and the server rejected it -- the server's own
   *  reason. Only ever present alongside `item.cannotEdit`, and the difference between
   *  "wrong password" and "no password" is the whole diagnostic. See util/saveOutcome.ts. */
  overrideRejected?: string;
};

/** A list-price write handed from create-assembly to finish-assembly (see deferPrice). */
export type PendingPrice = {
  price: number;
  source: "cpq2:submit" | "cpq2:edit";
  reference?: string | null;
  recordType?: string;
};

/**
 * Create the assembly in NetSuite: the serialized assembly item, its BOM (+
 * revision/components), and the manufacturing routing (steps). dryRun=true
 * returns the planned REST payloads without writing — use it to validate first.
 */
export async function createAssembly(
  payload: {
    item: { itemid: string; fields: Record<string, string> };
    bom?: unknown;
    routing?: unknown;
    // Full configuration JSON stored on the item for the Item Template shortcut.
    config?: string;
    // custitem_unfinishedconfiguration: a field outside Customer Requirements is blank
    // (util/unfinishedConfig). Written only alongside `config`, which it describes.
    unfinished?: boolean;
    // Create only the item and return fast; build BOM + routing via finishAssembly.
    itemOnly?: boolean;
    // Leave the custitem_* mapping fields off the POST and hand them back in
    // `deferredFields`, for finish-assembly to PATCH in the background. The fingerprint
    // and the config snapshot are written anyway -- dedup and reload depend on them.
    // Falls back to the full body automatically if the trimmed one is rejected.
    deferFields?: boolean;
    /** Pre-resolved part number from find-item?plan=1. Advisory: the server re-verifies it
     *  and resolves from scratch if anything has moved. */
    plannedName?: PlannedName | null;
    /** Admin override code for custitem_cannoteditcpq. Re-validated server-side on THIS
     *  request; the UI having unlocked grants nothing. */
    adminOverride?: string | null;
    // Edit Config: internal id of the exact item to update (skips dedup so the
    // volatile itemid hash can't spawn a duplicate item/BOM/route).
    editItemId?: string | number;
    /** The item Edit Config was LAUNCHED on. Sent on every submit and never cleared, so the
     *  server can enforce "a launched, unlocked, same-base item is edited, not duplicated"
     *  even when the UI has dropped out of Edit mode (a Favorite load still does). This is
     *  what stops one configuration becoming -1, -2 and -3. */
    launchedItemId?: string | number;
    // Sales price resolved from the NDB price list (see src/util/ndbPricing.ts).
    // Omitted when the four price-list keys don't resolve to a single price, in
    // which case create-assembly keeps its placeholder default.
    basePrice?: number;
    // itemOnly: hand the price write back as `pendingPrice` for finish-assembly instead of
    // making it here -- the sales order line never reads the item's price, so it does not
    // belong in the user's wait. An older server ignores it and prices as before.
    deferPrice?: boolean;
    // Patch ONLY the price on editItemId and return — no rename, description,
    // BOM or routing. Used by the Existing Config path (see setItemBasePrice).
    priceOnly?: boolean;
    // Audit context for the custitem_pricechangenotes line the server writes.
    // Every cpq2 request authenticates as the SAME integration user, so NetSuite's
    // own audit trail cannot distinguish salespeople — the operator has to be
    // carried through from the launch URL or "what changed it" is worthless.
    actor?: { name: string | null; id: string | null };
    // Which price-list key produced the number, so a disputed price is traceable.
    priceReference?: string | null;
  },
  opts: { dryRun?: boolean } = {}
): Promise<CreateAssemblyResult> {
  const res = await fetch(`${API_BASE}/create-assembly`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ ...payload, dryRun: opts.dryRun ?? false }),
  });
  const body = (await res.json().catch(() => ({}))) as CreateAssemblyResult & {
    error?: string;
    detail?: string;
  };
  if (!res.ok || body.ok === false) {
    // Surface NetSuite's detailed reason (not just the generic line) so the
    // modal shows exactly which field/value it rejected. Attach the HTTP status
    // so the caller can tell an ambiguous/gateway failure (502/504) — worth
    // retrying, since this endpoint is idempotent — from a real rejection.
    const msg = [body.error, body.detail].filter(Boolean).join("\n");
    const err = new Error(msg || `Create failed (HTTP ${res.status})`) as Error & {
      httpStatus?: number;
      /** The endpoint's own failure step, when it reported one. */
      step?: string;
      /** True when NetSuite REJECTED the data -- a definite answer, not an ambiguous
       *  gateway failure. create-assembly returns 502 for EVERY NetSuite failure,
       *  including a 400 "Unable to parse value 'false' from the Boolean field", so
       *  the status alone cannot tell the two apart. Retrying a rejection just
       *  repeats it and reports it as a timeout, which is what hid the real cause of
       *  every failed Flame Cell create. */
      rejected?: boolean;
      /** True when the response carried NO explanation at all — neither `error` nor
       *  `detail`, so the message below is the bare "Create failed (HTTP nnn)" line.
       *
       *  This is the signal that the response did not come from create-assembly.
       *  Every failure the endpoint can SEE it answers as JSON with a non-empty
       *  `error` (describeError never returns ""), and its logging is guarded so the
       *  catch itself cannot throw. So an empty body means the Azure host answered
       *  instead of the function — the worker was killed or the ~45s SWA execution
       *  limit was hit — and whether the work completed inside NetSuite is UNKNOWN.
       *  Callers must treat it as ambiguous and go and look, not as a failure. */
      opaque?: boolean;
      /** The endpoint's OWN verdict on whether re-running can help, when it gave one.
       *  Outranks every inference below, in both directions. */
      retryable?: boolean;
    };
    err.httpStatus = res.status;
    err.step = typeof body.step === "string" ? body.step : undefined;
    // A structured {ok:false, step, detail} body means the server reached NetSuite and
    // NetSuite answered. A bare gateway 502/504 has no such body.
    err.rejected = body.ok === false && !!body.step;
    err.opaque = !msg;
    // An EXPLICIT retryable flag from the endpoint outranks everything inferred here. The
    // inference is coarse: anything with a `step` counts as a rejection, which is right for
    // "NetSuite refused this value" and wrong for "NetSuite did not answer when we read the
    // item" -- a step-carrying 502 that is purely transient. The server knows which it is.
    const stated = (body as unknown as { retryable?: unknown }).retryable;
    if (typeof stated === "boolean") err.retryable = stated;
    throw err;
  }
  return body;
}

/** Is this create failure AMBIGUOUS — i.e. might the item exist anyway?
 *
 *  create-assembly is idempotent (it dedups on custitem_fingerprint), so the only
 *  question that matters after a failure is whether to go and LOOK for the item or
 *  report the failure as final. Three things mean "go and look":
 *
 *    • the connection never delivered an answer (timeout / network),
 *    • an SWA gateway 502/504,
 *    • an OPAQUE 5xx — a response carrying no explanation, which cannot have come
 *      from create-assembly, because every failure it can see it answers as JSON
 *      naming the cause. The Azure host answered instead, having killed the
 *      function (in practice the ~45s execution limit), and the work may well have
 *      completed inside NetSuite first.
 *
 *  A REJECTION is never ambiguous, whatever status it arrived under: create-assembly
 *  answers 502 for every NetSuite failure including a plain 400 "wrong type", so
 *  status alone would classify a definite rejection as "NetSuite was slow", retry it,
 *  probe for an item that was never going to exist, and then blame a timeout.
 *
 *  Lives here, next to the code that SETS these flags, so the rule and the flags
 *  cannot drift apart — and so it can be tested without a browser (test:recovery). */
export function isAmbiguousCreateFailure(e: unknown): boolean {
  const msg = e instanceof Error ? e.message : String(e);
  const get = <T,>(k: string): T | undefined =>
    e && typeof e === "object" && k in e ? (e as Record<string, T>)[k] : undefined;
  // The endpoint's OWN verdict wins, in both directions. It knows things this function can
  // only guess at -- notably that a step-carrying 502 from the verifyItem read is transient
  // rather than a rejection, which is why Luke was told to re-run by hand on 2026-08-20
  // instead of it being retried for him.
  const explicit = get<boolean>("retryable");
  if (typeof explicit === "boolean") return explicit;
  // A TIMEOUT is ambiguous whatever else the response says, so it is tested FIRST.
  // `rejected` is set from {ok:false, step}, and create-assembly's outermost wrapper
  // labels everything it catches step:"handler" — including the invocation-budget
  // error thrown by a NetSuite call that ran out of time. Checking `rejected` first
  // would classify precisely the case we most need to probe ("we stopped waiting, so
  // we do not know whether the item was made") as "NetSuite said no, do not look".
  if (/timed out|failed to fetch|network/i.test(msg)) return true;
  if (get<boolean>("rejected") === true) return false;
  const status = get<number>("httpStatus");
  if (status === 502 || status === 504) return true;
  return get<boolean>("opaque") === true && typeof status === "number" && status >= 500;
}

// ---- Customer Requirements (customrecord_customer_requirements) ----

export type CrCreateResult = { ok: boolean; id?: string; dryRun?: boolean; body?: unknown };

/** Create the Customer Requirements record from a finished configuration. */
export async function createCustomerRequirements(
  fields: Record<string, string | number | null>,
  references: string[],
  opts: { dryRun?: boolean } = {}
): Promise<CrCreateResult> {
  const res = await fetch(`${API_BASE}/customer-requirements`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ fields, references, dryRun: opts.dryRun ?? false }),
  });
  const body = (await res.json().catch(() => ({}))) as CrCreateResult & {
    error?: string;
    detail?: string;
  };
  if (!res.ok || body.ok === false) {
    const msg = [body.error, body.detail].filter(Boolean).join("\n");
    throw new Error(msg || `Customer Requirements create failed (HTTP ${res.status})`);
  }
  return body;
}

// ---- Item Template: fetch an existing item's mapping by item number ----

export type ItemMapping = {
  ok: boolean;
  id: string;
  itemid: string | null;
  mapping: string;
  // Full configuration snapshot (JSON of the answer state), when the item has it.
  config?: string;
  // The item's custiteminlet_connection_notes, mapped back into the editable
  // INLET_CONNECTION_NOTES question on load.
  inletNotes?: string;
  // True when the item is flagged custitem_cannoteditcpq or sits on two or more sales
  // orders: Edit Config must not update it in place — cpq2 switches to New Config instead.
  cannotEdit?: boolean;
  /** Why, in words ("on 2 sales orders (this one and 202609-927)"). */
  lockReason?: string | null;
  /** "fields" when the server built the mapping from the item's record fields (a legacy part
   *  with no saved configuration and no mapping). */
  mappingFrom?: string;
};

// BOOT CACHE — exactly ONE entry, consumed once.
//
// On an Edit Config launch the app has to read the launch item TWICE in quick
// succession: App fetches it to learn which configurator built it (the product
// stamp lives in its snapshot), and a moment later AutoLoadItem fetches the same
// item to apply that configuration. NetSuite REST serialises at concurrency 1, so
// the second round-trip would just double the wait the user already spends on
// "Loading saved configuration…".
//
// Deliberately SINGLE-USE rather than a general cache: the first read drains it, so
// loading the same item again later (re-running Item Template after a save, say)
// goes to the network and cannot be served a stale configuration.
let bootItem: { key: string; payload: Promise<ItemMapping> } | null = null;

/** Fetch an item and stash it for the ONE next fetchItemMapping of the same id. */
export function primeItemMapping(itemNumber: string): Promise<ItemMapping> {
  const key = String(itemNumber);
  const payload = fetchItemMappingFromNetwork(key);
  // Swallow nothing: the caller handles rejection, and the stored promise keeps the
  // rejection so the consumer sees the same failure rather than refetching.
  bootItem = { key, payload };
  return payload;
}

/**
 * Look up an item by its number (itemid) and return its custitem_mapping for the
 * Item Template shortcut. Throws with the proxy's error message on failure.
 */
export async function fetchItemMapping(itemNumber: string): Promise<ItemMapping> {
  if (bootItem && bootItem.key === String(itemNumber)) {
    const cached = bootItem.payload;
    bootItem = null; // single use
    return cached;
  }
  return fetchItemMappingFromNetwork(itemNumber);
}

async function fetchItemMappingFromNetwork(itemNumber: string): Promise<ItemMapping> {
  // Where this launch came from, for the two-orders lock (api/shared/item-lock.js): the
  // launching order is one of the two, any other launch needs two.
  const ctx = getLaunchContext();
  const q = new URLSearchParams({ itemid: itemNumber });
  if (ctx.originType) q.set("trantype", ctx.originType);
  if (ctx.transactionId) q.set("transaction", ctx.transactionId);
  const res = await fetch(`${API_BASE}/ns-item-mapping?${q.toString()}`, { headers: { Accept: "application/json" } });
  const body = (await res.json().catch(() => ({}))) as ItemMapping & { error?: string };
  if (!res.ok || body.ok === false) {
    throw new Error(body.error || `Item lookup failed (HTTP ${res.status})`);
  }
  return body;
}

// ---- Customer Part Number (customrecord_scm_customerpartnumber) ----

export type CpnHolder = {
  id: string;
  itemId: string | null;
  itemName: string | null;
  customerId: string | null;
  // Only populated when the check was made with `detail` (and the record type
  // exposes them) — see the DETAIL_COLS note in api/customer-part-number.
  customerName: string | null;
  createdBy: string | null;
  createdOn: string | null;
};
export type CpnCheck = {
  ok: boolean;
  exists: boolean;
  holders: CpnHolder[];
  nextFreeName: string;
  // False when the enriched query was asked for but unavailable, so the caller
  // knows customerName / createdBy are missing rather than genuinely blank.
  detail?: boolean;
};

/**
 * Read-only check: does `name` already exist as a Customer Part Number, who
 * holds it, and what the existing holder would be renamed to. Used to prompt
 * the user before Save & Submit. Resolves to null if the proxy is unreachable.
 *
 * `detail` also asks for the holder's customer name, who created the record and
 * when — for the live in-use status under the cpq2 field.
 */
export async function checkCustomerPartNumber(
  name: string,
  opts: { detail?: boolean; timeoutMs?: number } = {}
): Promise<CpnCheck | null> {
  const qs = new URLSearchParams({ name });
  if (opts.detail) qs.set("detail", "1");
  // BOUNDED. getJson has no timeout, so a stalled request leaves its promise
  // pending forever — which is what left the in-use status stuck on "Checking
  // availability…" with no answer. A lookup that can't finish must RESOLVE
  // (as null) so the caller can say so, rather than hang silently.
  const timeoutMs = opts.timeoutMs ?? 12000;
  const ctl = new AbortController();
  const timer = window.setTimeout(() => ctl.abort(), timeoutMs);
  try {
    const res = await fetch(`${API_BASE}/customer-part-number?${qs.toString()}`, {
      headers: { Accept: "application/json" },
      signal: ctl.signal,
    });
    const body = (await res.json().catch(() => ({}))) as CpnCheck & { error?: string };
    if (!res.ok || body.ok === false) return null;
    return body;
  } catch {
    return null; // network error, abort, or unparseable body
  } finally {
    window.clearTimeout(timer);
  }
}

/**
 * Which Customer Part Number does this item already hold? Used to PRE-FILL the
 * cpq2 CPN field (the "then item" fallback when the launched sales-order line
 * carries no CPN). Prefers the record scoped to this customer, else any CPN for
 * the item. Best-effort: null on any proxy failure (so nothing is pre-filled).
 */
export async function fetchItemCustomerPartNumber(
  itemId: string,
  customerId?: string | null
): Promise<string | null> {
  const id = String(itemId ?? "").trim();
  if (!id) return null;
  try {
    const qs = new URLSearchParams({ itemId: id });
    if (customerId) qs.set("customerId", String(customerId));
    const body = await getJson<{ ok: boolean; cpn: { id: string; name: string } | null }>(
      `/customer-part-number?${qs.toString()}`
    );
    return body.ok && body.cpn && body.cpn.name ? String(body.cpn.name) : null;
  } catch {
    return null;
  }
}

export type CpnCreateResult = {
  ok: boolean;
  dryRun?: boolean;
  created?: { id: string; name: string };
  renamed?: Array<{ id: string; to: string; itemName: string | null }>;
  willRename?: Array<{ id: string; to: string; itemName: string | null }>;
};

/**
 * Create the Customer Part Number record for an item. If `partNumber` is already
 * taken, its current holder is renamed to the next free "partNumber_N" and the
 * clean number is assigned to this item. dryRun=true returns the plan only.
 */
export async function createCustomerPartNumber(
  payload: {
    partNumber: string;
    itemId: string | number;
    customerId?: string | number | null;
    subsidiaryId?: string | number | null;
    description?: string;
    notes?: string;
  },
  opts: { dryRun?: boolean; timeoutMs?: number } = {}
): Promise<CpnCreateResult> {
  // BOUNDED, and that is what makes this function's error handling actually work.
  //
  // Kevin, 2026-09-17: "after i press save and submit sometimes the line item fails to get
  // added to the line items ... the cause of this is when I give something a customer part
  // number." This call is AWAITED on the path to postConfiguredLine, and its caller wraps it
  // in a try/catch on the stated grounds that "a failure must never block the line". A HANG
  // is not a failure: nothing rejects, the catch never runs, and the line is never posted.
  //
  // It hangs for a reason that has nothing to do with this request being wrong. NetSuite REST
  // is concurrency 1, and by the time this fires the background BOM/routing build already
  // holds the connection for several sequential calls -- so this one QUEUES behind it, and a
  // queued second request has been measured at ~61s. Past the gateway's patience the promise
  // simply never settles, and the user is left on a sales order with no line on it.
  //
  // Bounded at 20s: long enough to win the record id in the ordinary case, short enough that
  // nobody is stranded. On timeout this REJECTS, so the caller's existing catch does exactly
  // what it already says -- carry on without the id. The part number still reaches the line
  // as display text, and the server may well finish creating the record anyway; what is lost
  // is the select column's link, not the line.
  const timeoutMs = opts.timeoutMs ?? 20000;
  const ctl = new AbortController();
  const timer = window.setTimeout(() => ctl.abort(), timeoutMs);
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/customer-part-number`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ ...payload, dryRun: opts.dryRun ?? false }),
      signal: ctl.signal,
    });
  } catch (e) {
    throw new Error(
      ctl.signal.aborted
        ? `Customer Part Number create did not answer within ${Math.round(timeoutMs / 1000)}s`
        : `Customer Part Number create failed: ${e instanceof Error ? e.message : String(e)}`
    );
  } finally {
    window.clearTimeout(timer);
  }
  const body = (await res.json().catch(() => ({}))) as CpnCreateResult & {
    error?: string;
    detail?: string;
  };
  if (!res.ok || body.ok === false) {
    const msg = [body.error, body.detail].filter(Boolean).join("\n");
    throw new Error(msg || `Customer Part Number create failed (HTTP ${res.status})`);
  }
  return body;
}

/**
 * Batch item lookup by internal id (used by the operation runtime's getData).
 * Returns the items found; resolves to [] on any failure (e.g. the /api proxy
 * isn't reachable in plain `vite dev`), so callers degrade gracefully.
 */
export async function nsItems(
  ids: Array<string | number>
): Promise<NsItem[]> {
  if (!ids.length) return [];
  try {
    const qs = ids.map((id) => encodeURIComponent(String(id))).join(",");
    const body = await getJson<{ ok: boolean; items: NsItem[] }>(`/ns-items?ids=${qs}`);
    return body.items ?? [];
  } catch {
    return [];
  }
}
